// Top winners carousel.
//
// Scroll drives a slide-in from the right; once the entry completes the track
// latches into a continuous auto-scrolling loop. Pointer drags add a persistent
// offset on top of either phase. The shadow strip mirrors the ticket track in
// the opposite direction and is always wrapped, so it never reads as empty.

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
const mod = (value, span) => ((value % span) + span) % span;

// Exponential smoothing that is independent of frame rate.
const approach = (current, target, response, seconds) =>
  current + (target - current) * (1 - Math.exp(-response * seconds));

const TopWinnersCarouselHook = {
  mounted() {
    this.track = this.el.querySelector("[data-carousel-track]");
    this.shadow = this.el.querySelector("[data-carousel-shadow]");
    this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    this.speed = Number(this.el.dataset.speed || 55);
    // Share of the viewport height the section rises through before the
    // slide-in hands off to the loop. Lower starts the auto-scroll sooner.
    this.entryTravel = Math.max(
      Number(this.el.dataset.entryTravel || 0.45),
      0.05,
    );
    this.progress = 0;
    this.latched = false;
    this.autoX = 0;
    this.manualTarget = 0;
    this.manualX = 0;
    this.dragging = false;
    this.visible = false;
    this.frame = null;
    this.lastTime = 0;

    this.el
      .querySelectorAll("img")
      .forEach((image) => (image.draggable = false));

    this.fillTracks();
    this.measure();
    this.bindEvents();
    this.draw();
  },

  destroyed() {
    cancelAnimationFrame(this.frame);
    this.observer?.disconnect();
    this.resizeObserver?.disconnect();
    this.el.removeEventListener("wheel", this.onWheel);
    window.removeEventListener("pointermove", this.onPointerMove);
    window.removeEventListener("pointerup", this.onPointerUp);
    window.removeEventListener("pointercancel", this.onPointerUp);
  },

  // Duplicate each track until it can cover a full cycle plus the viewport, so
  // wrapping never exposes a gap.
  fillTracks() {
    [this.track, this.shadow].forEach((row) => {
      const originals = [...row.children];
      if (!originals.length) return;

      const needed = row.scrollWidth + this.el.clientWidth;
      let guard = 0;

      while (row.scrollWidth < needed && guard < 12) {
        originals.forEach((node) => {
          const clone = node.cloneNode(true);
          clone.setAttribute("aria-hidden", "true");
          clone
            .querySelectorAll("[id]")
            .forEach((el) => el.removeAttribute("id"));
          clone.removeAttribute("id");
          row.appendChild(clone);
        });
        guard += 1;
      }

      row.dataset.cycleItems = String(originals.length);
    });
  },

  measure() {
    this.entryDistance = this.el.clientWidth;
    this.cycle = this.cycleWidth(this.track);
    this.shadowCycle = this.cycleWidth(this.shadow);
  },

  // Pitch of one original set: its own width plus the gap that follows it.
  cycleWidth(row) {
    const count = Number(row.dataset.cycleItems || 0);
    if (!count) return 1;

    const gap = parseFloat(getComputedStyle(row).columnGap) || 0;
    const children = [...row.children].slice(0, count);
    const width = children.reduce(
      (total, node) => total + node.getBoundingClientRect().width,
      0,
    );

    return Math.max(width + gap * count, 1);
  },

  readProgress() {
    const rect = this.el.getBoundingClientRect();
    const viewportHeight =
      window.innerHeight || document.documentElement.clientHeight;

    return clamp(
      (viewportHeight - rect.top) / (viewportHeight * this.entryTravel),
      0,
      1,
    );
  },

  bindEvents() {
    this.observer = new IntersectionObserver(
      ([entry]) => {
        this.visible = entry.isIntersecting;
        if (this.visible) this.start();
      },
      { rootMargin: "200px 0px" },
    );
    this.observer.observe(this.el);

    this.resizeObserver = new ResizeObserver(() => {
      this.measure();
      this.draw();
    });
    this.resizeObserver.observe(this.el);

    this.el.addEventListener("dragstart", (event) => event.preventDefault());

    this.onPointerMove = (event) => {
      if (!this.dragging) return;

      const deltaX = event.clientX - this.pointerStartX;
      const deltaY = event.clientY - this.pointerStartY;

      if (this.dragAxis === null && Math.hypot(deltaX, deltaY) > 6) {
        this.dragAxis =
          Math.abs(deltaX) > Math.abs(deltaY) ? "horizontal" : "vertical";
        if (this.dragAxis === "horizontal")
          this.el.classList.add("is-dragging");
      }

      if (this.dragAxis !== "horizontal") return;

      this.manualTarget = this.dragStartManual + deltaX;
      this.start();
    };

    this.onPointerUp = () => {
      if (!this.dragging) return;
      this.dragging = false;
      this.dragAxis = null;
      this.el.classList.remove("is-dragging");
      this.start();
    };

    this.el.addEventListener("pointerdown", (event) => {
      if (event.button !== 0) return;
      this.dragging = true;
      this.dragAxis = null;
      this.pointerStartX = event.clientX;
      this.pointerStartY = event.clientY;
      this.dragStartManual = this.manualTarget;
      this.start();
    });

    window.addEventListener("pointermove", this.onPointerMove, {
      passive: true,
    });
    window.addEventListener("pointerup", this.onPointerUp);
    window.addEventListener("pointercancel", this.onPointerUp);

    // Trackpad swipes and shift+wheel nudge the track; a mostly-vertical wheel
    // is left alone so the page keeps scrolling.
    this.onWheel = (event) => {
      const deltaX = event.shiftKey && !event.deltaX ? event.deltaY : event.deltaX;
      if (Math.abs(deltaX) <= Math.abs(event.deltaY) && !event.shiftKey) return;

      event.preventDefault();
      this.manualTarget -= deltaX;
      this.start();
    };

    this.el.addEventListener("wheel", this.onWheel, { passive: false });
  },

  start() {
    if (this.frame) return;
    this.lastTime = 0;
    this.frame = requestAnimationFrame(this.step.bind(this));
  },

  step(timestamp) {
    this.frame = null;

    const seconds = this.lastTime
      ? Math.min((timestamp - this.lastTime) / 1000, 0.05)
      : 1 / 60;
    this.lastTime = timestamp;

    const reduced = this.reducedMotion.matches;

    if (!this.latched) {
      const target = reduced ? 1 : this.readProgress();
      this.progress = reduced
        ? 1
        : approach(this.progress, target, 5.3, seconds);

      if (this.progress >= 0.999) {
        this.progress = 1;
        this.latched = true;
      }
    }

    if (this.latched && !this.dragging && !reduced) {
      this.autoX += this.speed * seconds;
    }

    this.manualX = approach(this.manualX, this.manualTarget, 9, seconds);
    if (Math.abs(this.manualTarget - this.manualX) < 0.1)
      this.manualX = this.manualTarget;

    this.draw();

    // Entry tracks scroll and the latched loop runs forever, so the only idle
    // state is reduced motion with no drag left to settle.
    const idle =
      reduced && !this.dragging && this.manualX === this.manualTarget;

    if (this.visible && !idle) {
      this.frame = requestAnimationFrame(this.step.bind(this));
    } else {
      this.lastTime = 0;
    }
  },

  draw() {
    const entry = (1 - this.progress) * this.entryDistance;
    const ticketX = entry - this.autoX + this.manualX;
    const shadowX =
      this.progress * this.entryDistance + this.autoX - this.manualX;

    const wrappedTicketX = this.latched
      ? mod(ticketX, this.cycle) - this.cycle
      : ticketX;
    const wrappedShadowX = mod(shadowX, this.shadowCycle) - this.shadowCycle;

    this.track.style.transform = `translate3d(${wrappedTicketX.toFixed(2)}px, 0, 0)`;
    this.shadow.style.transform = `translate3d(${wrappedShadowX.toFixed(2)}px, 0, 0)`;
  },
};

export default TopWinnersCarouselHook;
